import { defaultVoices, findVoiceById } from "@/lib/default_voices"
import { US_STATES } from "@/lib/locales"
import { ApiError, CallerSettingField, CallerSettings, VibratoClient, VoiceClone } from "@/lib/vibrato-api"
import { Copy, InfoCircle, Play } from "iconoir-react"
import { useContext, useEffect, useState, useRef } from "react"
import { useSearchParams } from "react-router-dom"
import CloneVoiceModal from "@/components/dashboard/CloneVoiceModal"
import { UpgradeModalContext, UserContext, VibratoClientContext } from "@/components/dashboard/contexts"
import { Tooltip } from "flowbite-react"
import { LoadingSpinner } from "@/components/LoadingSpinner"

export const idToVoice = (id: string) => {
  const voice = defaultVoices.find((voice) => voice.id === id)
  return voice
}

export const aiModels = [
  // { displayName: "High intelligence & high speed (Gemini 2.5 Flash)", value: "gemini-2.5-flash" },
  { displayName: "High intelligence & medium speed (GPT-4.1)", value: "gpt-4.1-mini" }
  // { displayName: "Medium intelligence & high speed (GPT-4.1-nano)", value: "gpt-4.1-nano" }
  // { displayName: "Medium intelligence (Llama 3.1 70b)", value: "llama-3p1-70b-instruct-fireworks" },
  // { displayName: "Medium intelligence (Llama 3 70b)", value: "llama-3-70b-instruct-fireworks" },
  // { displayName: "Low intelligence (Llama 3.1 8b)", value: "llama-3p1-8b-instruct-fireworks" }
]

const freemiumAiModels = [
  // { displayName: "High intelligence & high speed (Gemini 2.5 Flash)", value: "gemini-2.5-flash" },
  { displayName: "High intelligence & medium speed (GPT-4.1)", value: "gpt-4.1-mini" }
  // { displayName: "Medium intelligence & high speed (GPT-4.1-nano)", value: "gpt-4.1-nano" }
]

export default function VoiceSettings({ inModal }: { inModal?: boolean }) {
  return <CallerSettingsComponent inModal={inModal} />
}

function CallerSettingsComponent({ inModal }: { inModal?: boolean }) {
  const vibratoClient: VibratoClient = useContext(VibratoClientContext)
  const user = useContext(UserContext)
  const [plan, setPlan] = useState<any>(null)
  const upgradeModal = useContext(UpgradeModalContext)
  const [searchParams] = useSearchParams()

  const [, setCallerSettings] = useState<CallerSettings | null>(null)
  const [voiceClones, setVoiceClones] = useState<VoiceClone[]>([])

  // State variables to store form field values
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [email, setEmail] = useState("")
  const [address, setAddress] = useState("")
  const [city, setCity] = useState("")
  const [state, setState] = useState("")
  const [zip, setZip] = useState("")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [customFields, setCustomFields] = useState<CallerSettingField[]>([])
  const [selectedVoice, setSelectedVoice] = useState(defaultVoices[0].id)
  const [successMessage, setSuccessMessage] = useState("")
  const [errorMessage, setErrorMessage] = useState("")
  const [showCloneVoiceModal, setShowCloneVoiceModal] = useState(false)
  const [reloadVoiceClones, setReloadVoiceClones] = useState(0)
  const [aiModel, setAiModel] = useState<string>("gpt-4.1-nano")
  const [isSaving, setIsSaving] = useState(false)

  // Error fields
  const [firstNameError] = useState("")
  const [lastNameError] = useState("")
  const [phoneNumberError] = useState("")

  const audioPlayerRef = useRef<HTMLAudioElement | null>(null)

  // Add useEffect to handle message timeout
  useEffect(() => {
    if (successMessage || errorMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage("")
        setErrorMessage("")
      }, 5000)

      return () => clearTimeout(timer)
    }
  }, [successMessage, errorMessage])

  useEffect(() => {
    // Cleanup audio on component unmount
    return () => {
      if (audioPlayerRef.current) {
        audioPlayerRef.current.pause()
        audioPlayerRef.current.currentTime = 0
      }
    }
  }, [])

  useEffect(() => {
    vibratoClient
      .listVoiceClones()
      .then((data) => {
        setVoiceClones(data?.results || [])
      })
      .catch((error) => {
        console.error("Error fetching voice clones:", error)
      })
  }, [setVoiceClones, reloadVoiceClones])

  useEffect(() => {
    vibratoClient
      .getPlan()
      .then((data) => {
        setPlan(data)
      })
      .catch((error) => {
        console.error("Error fetching plan:", error)
      })
  }, [setPlan])

  useEffect(() => {
    vibratoClient
      .getCallerSettings()
      .then((data) => {
        setCallerSettings(data)
        if (data.voice_id) {
          setSelectedVoice(data.voice_id)
        }
        if (data.voice_clone) {
          setSelectedVoice("voice-clone")
        }
        if (data.ai_model_slug) {
          setAiModel(data.ai_model_slug)
        }
        data.standard_fields.forEach((field) => {
          switch (field.name) {
            case "first_name":
              setFirstName(field.value)
              break
            case "last_name":
              setLastName(field.value)
              break
            case "email":
              setEmail(field.value)
              break
            case "address":
              setAddress(field.value)
              break
            case "city":
              setCity(field.value)
              break
            case "state":
              setState(field.value)
              break
            case "zip":
              setZip(field.value)
              break
            case "phone_number":
              setPhoneNumber(field.value)
              break

            default:
              break
          }
        })
        data.custom_fields.forEach((field) => {
          setCustomFields([...customFields, { name: field.name, description: field.description, value: field.value }])
        })
      })
      .catch((error) => {
        console.error("Error fetching caller settings:", error)
      })
  }, [
    setCallerSettings,
    setFirstName,
    setLastName,
    setEmail,
    setAddress,
    setCity,
    setState,
    setZip,
    setPhoneNumber,
    setSelectedVoice,
    setCustomFields
  ])

  useEffect(() => {
    // Check URL parameter to show clone voice modal
    if (searchParams.get("clone") === "true") {
      if (["freemium", "lite"].includes(user.plan)) {
        // upgradeModal.setError("You must upgrade to Vibrato Plus or Vibrato for Business to clone a voice.")
        // upgradeModal.setShowModal(true)
      } else {
        setShowCloneVoiceModal(true)
      }
    }
  }, [searchParams, user.plan, upgradeModal])

  // Function to handle form submission
  const handleSubmit = (event: any) => {
    event.preventDefault()

    // if (!validateFields()) {
    //   setErrorMessage("Error saving caller settings.")
    //   setSuccessMessage("")
    //   return
    // }

    setIsSaving(true)
    setErrorMessage("")
    setSuccessMessage("")

    // Preprocess phone number to strip all non-numeric characters
    const processedPhoneNumber = phoneNumber.replace(/\D/g, "")

    // create fields
    const fields = [
      { name: "first_name", description: "First Name", value: firstName },
      { name: "last_name", description: "Last Name", value: lastName },
      { name: "email", description: "Email", value: email },
      { name: "address", description: "Address", value: address },
      { name: "city", description: "City", value: city },
      { name: "state", description: "State", value: state },
      { name: "zip", description: "Zip Code", value: zip },
      { name: "phone_number", description: "Phone Number", value: processedPhoneNumber }
    ].filter((field) => field.value !== "")

    const updated: CallerSettings = { standard_fields: fields, custom_fields: customFields, ai_model_slug: aiModel }

    if (selectedVoice) {
      if (selectedVoice === "voice-clone") {
        updated.voice_clone = voiceClones[0].id
        updated.voice_id = null
      } else {
        updated.voice_id = selectedVoice
        updated.voice_clone = null
      }
    }

    vibratoClient
      .updateCallerSettings(updated)
      .then(() => {
        setErrorMessage("")
        setSuccessMessage("Settings saved successfully.")
      })
      .catch((error: any) => {
        console.error("Error saving caller settings:", error)
        if (error instanceof ApiError) {
          setErrorMessage(`Error saving caller settings: ${JSON.stringify(error.data)}`)
        } else {
          setErrorMessage("Error saving caller settings.")
        }
      })
      .finally(() => {
        setIsSaving(false)
      })
  }

  const handleCloneVoice = () => {
    if (["freemium", "lite", "standard"].includes(user.plan)) {
      upgradeModal.setShowModal(true)
      upgradeModal.setError("You must upgrade to Vibrato Plus or Vibrato for Business to clone a voice.")
      return
    }

    setShowCloneVoiceModal(true)
  }

  const showCredits = plan && ["pro", "scale", "enterprise", "standard", "lite"].includes(plan.plan_slug)
  const showAiSettings = true

  const handlePlayVoice = () => {
    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause()
      audioPlayerRef.current.currentTime = 0
    }

    if (selectedVoice === "voice-clone") {
      const vc = voiceClones[0]
      if (!vc.sample_audio_url) {
        console.error("Voice clone has no sample audio")
        return
      }

      const audio = new Audio(vc.sample_audio_url)
      console.log(vc, vc.sample_audio_url)
      audio.play()
      audioPlayerRef.current = audio
      return
    }

    if (selectedVoice) {
      const voice = defaultVoices.find((voice) => voice.id === selectedVoice)
      if (voice) {
        const audio = new Audio(voice.clip_url)
        audio.play()
        audioPlayerRef.current = audio
      }
    } else {
      console.log("No voice selected")
    }
  }

  const reloadVC = () => {
    setReloadVoiceClones((prev) => prev + 1)
  }

  return (
    <div className="w-full flex flex-col gap-6">
      {showCloneVoiceModal && (
        <CloneVoiceModal show={showCloneVoiceModal} setShow={setShowCloneVoiceModal} reload={reloadVC} />
      )}
      <form onSubmit={handleSubmit} className="space-y-6 relative">
        {!inModal && (
          <div className="flex justify-center">
            <SummaryDashboard
              planDetails={plan}
              modelSlug={aiModel}
              voiceCloneId={selectedVoice === "voice-clone" ? "voice-clone" : null}
              voiceId={selectedVoice === "voice-clone" ? null : selectedVoice}
              showCredits={showCredits}
            />
          </div>
        )}

        {showAiSettings && (
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="flex items-center mb-2">
              <h2 className="text-xl font-semibold text-gray-800">AI Settings</h2>
              <Tooltip content="Select the AI model that powers your calls. More intelligent models provide better understanding and problem-solving but may use more credits.">
                <InfoCircle className="ml-2 text-gray-500 cursor-help" width="18" height="18" />
              </Tooltip>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Control the intelligence and speed of your AI. Higher intelligence models can handle more complex
              conversations, but may have slightly longer response times.
            </p>
            <AIIntelligenceVisualization modelSlug={aiModel} />
            <div className="mt-4 mb-4">
              <label htmlFor="aiModel" className="block text-sm font-medium text-gray-700 mb-1">
                Choose AI Model
              </label>
              <select
                id="aiModel"
                value={aiModel}
                onChange={(e) => setAiModel(e.target.value)}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 appearance-none overflow-hidden text-ellipsis"
              >
                {user.plan != "freemium" &&
                  aiModels.map((model) => (
                    <option key={model.value} value={model.value}>
                      {model.displayName}
                    </option>
                  ))}
                {user.plan == "freemium" &&
                  freemiumAiModels.map((model) => (
                    <option key={model.value} value={model.value}>
                      {model.displayName}
                    </option>
                  ))}
              </select>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center mb-2">
            <h2 className="text-xl font-semibold text-gray-800">Voice Settings</h2>
            <Tooltip content="Choose the voice that represents you on calls. Premium voices and voice cloning offer the most natural-sounding experience. Try the preview to hear how it sounds.">
              <InfoCircle className="ml-2 text-gray-500 cursor-help" width="18" height="18" />
            </Tooltip>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Choose the voice for your AI. You can select from our library of realistic voices or clone your own for a
            personal touch.
          </p>
          <div className="space-y-4">
            <VoiceRealismVisualization
              voiceCloneId={selectedVoice === "voice-clone" ? "voice-clone" : null}
              voiceId={selectedVoice === "voice-clone" ? null : selectedVoice}
            />
            <div>
              <label htmlFor="voice" className="block text-sm font-medium text-gray-700 mb-1">
                Choose a Voice
              </label>
              <select
                id="voice"
                value={selectedVoice}
                onChange={(e) => setSelectedVoice(e.target.value)}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 appearance-none"
                style={{
                  backgroundImage:
                    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23666666' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E\")",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 1rem center",
                  backgroundSize: "1em"
                }}
              >
                {voiceClones.length > 0 && (
                  <option key="voice-clone" value="voice-clone">
                    Voice clone (Premium+, any language)
                  </option>
                )}
                {defaultVoices.map((voice) => (
                  <option key={voice.id} value={voice.id}>
                    {voice.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col md:flex-row gap-3">
              <button
                type="button"
                onClick={handlePlayVoice}
                className="flex items-center justify-center px-4 py-3 bg-purple-600 rounded-xl text-white font-medium shadow-sm hover:bg-purple-700 active:bg-purple-800 w-full md:w-auto md:flex-1"
              >
                <Play className="mr-2" width="18" height="18" />
                Preview Voice
              </button>

              <button
                type="button"
                onClick={handleCloneVoice}
                className="flex items-center justify-center px-4 py-3 bg-blue-600 rounded-xl text-white font-medium shadow-sm hover:bg-blue-700 active:bg-blue-800 w-full md:w-auto md:flex-1"
              >
                <Copy className="mr-2" width="18" height="18" />
                Clone Voice
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center mb-2">
            <h2 className="text-xl font-semibold text-gray-800">Personal Details</h2>
            <Tooltip content="Your personal information is used by our AI during calls to accurately represent you. We only share what's necessary for the specific call context.">
              <InfoCircle className="ml-2 text-gray-500 cursor-help" width="18" height="18" />
            </Tooltip>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            This information helps your AI introduce itself and handle personal questions during calls. It's stored
            securely and only used when needed.
          </p>

          <div className="space-y-4">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                First Name
              </label>
              <input
                type="text"
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              {firstNameError && <p className="mt-1 text-sm text-red-600">{firstNameError}</p>}
            </div>

            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                Last Name
              </label>
              <input
                type="text"
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              {lastNameError && <p className="mt-1 text-sm text-red-600">{lastNameError}</p>}
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                id="email"
                value={user.email}
                disabled
                className="w-full px-4 py-3 bg-gray-100 border border-gray-300 rounded-xl text-gray-500 cursor-not-allowed"
              />
            </div>

            <div>
              <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number
              </label>
              <input
                type="tel"
                id="phoneNumber"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="(555) 123-4567"
              />
              {phoneNumberError && <p className="mt-1 text-sm text-red-600">{phoneNumberError}</p>}
            </div>

            <div>
              <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                Address
              </label>
              <input
                type="text"
                id="address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            <div className="space-y-4 md:grid md:grid-cols-3 md:gap-4 md:space-y-0">
              <div>
                <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                  City
                </label>
                <input
                  type="text"
                  id="city"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div>
                <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1">
                  State
                </label>
                <select
                  id="state"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 appearance-none"
                  style={{
                    backgroundImage:
                      "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23666666' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E\")",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 1rem center",
                    backgroundSize: "1em"
                  }}
                >
                  <option value="">Select a state</option>
                  {US_STATES.map((usState) => (
                    <option key={usState} value={usState}>
                      {usState}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="zip" className="block text-sm font-medium text-gray-700 mb-1">
                  Zip Code
                </label>
                <input
                  type="text"
                  id="zip"
                  value={zip}
                  onChange={(e) => setZip(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Status messages and save button */}
        <div className="flex flex-col items-center">
          {errorMessage && (
            <div className="w-full mb-2 p-3 bg-red-100 border border-red-200 rounded-xl">
              <p className="text-sm text-red-700" id="error-message">
                {errorMessage}
              </p>
            </div>
          )}

          {successMessage && (
            <div className="w-full mb-2 p-3 bg-green-100 border border-green-200 rounded-xl">
              <p className="text-sm text-green-700" id="success-message">
                {successMessage}
              </p>
            </div>
          )}

          <button
            type="submit"
            disabled={isSaving}
            className={`w-full py-2 px-4 rounded-xl text-white font-medium text-lg shadow-sm transition duration-150 flex items-center justify-center gap-2 ${
              isSaving ? "bg-gray-400 cursor-not-allowed" : "bg-green-500 hover:bg-green-600 active:bg-green-700"
            }`}
          >
            {isSaving && <LoadingSpinner className="w-5 h-5" />}
            {isSaving ? "Saving..." : "Save Settings"}
          </button>
        </div>
      </form>
    </div>
  )
}

export const modelAttributes: Record<string, { speed: number; intelligence: number }> = {
  "gemini-2.5-flash": { speed: 200, intelligence: 95 },
  "gpt-4.1-mini": { speed: 400, intelligence: 95 },
  "gpt-4.1-nano": { speed: 200, intelligence: 75 },
  "llama-3p1-8b-instruct-fireworks": { speed: 350, intelligence: 60 },
  "llama-3-70b-instruct-fireworks": { speed: 500, intelligence: 70 },
  "llama-3p1-70b-instruct-fireworks": { speed: 500, intelligence: 80 },
  "llama-3p1-405b-instruct-fireworks": { speed: 700, intelligence: 90 }
}

export const voiceAttributes: Record<string, { speed: number; realism: number }> = {
  elevenlabs: { speed: 150, realism: 95 },
  gcp: { speed: 100, realism: 70 }
}

function SummaryDashboard({
  planDetails,
  modelSlug,
  voiceCloneId,
  voiceId,
  showCredits
}: {
  planDetails: any
  modelSlug: string
  voiceCloneId: string | null
  voiceId: string | null
  showCredits: boolean
}) {
  const [modelCreditsPerMinute, setModelCreditsPerMinute] = useState(0)
  const [voiceCreditsPerMinute, setVoiceCreditsPerMinute] = useState(0)
  const [modelCreditsWidth, setModelCreditsWidth] = useState("")
  const [voiceCreditsWidth, setVoiceCreditsWidth] = useState("")
  const [modelSpeed, setModelSpeed] = useState(50)
  const [voiceSpeed, setVoiceSpeed] = useState(0)
  const [modelSpeedWidth, setModelSpeedWidth] = useState("")
  const [voiceSpeedWidth, setVoiceSpeedWidth] = useState("")
  const [maxCredits, setMaxCredits] = useState(0)

  useEffect(() => {
    if (!planDetails) {
      return
    }

    const elevenlabsCost = planDetails.tts_credits_per_minute?.elevenlabs || 0
    const gcpCost = planDetails.tts_credits_per_minute?.gcp || 0

    if (voiceCloneId) {
      setVoiceCreditsPerMinute(elevenlabsCost)
      setVoiceSpeed(voiceAttributes["elevenlabs"].speed)
    } else if (voiceId) {
      const voiceMeta = findVoiceById(voiceId)
      if (!voiceMeta) {
        console.error("Voice not found")
        return
      }

      if (voiceMeta.provider === "gcp") {
        setVoiceCreditsPerMinute(gcpCost)
        setVoiceSpeed(voiceAttributes["gcp"].speed)
      } else if (voiceMeta.provider === "elevenlabs") {
        setVoiceCreditsPerMinute(elevenlabsCost)
        setVoiceSpeed(voiceAttributes["elevenlabs"].speed)
      } else {
        console.error("Unknown voice provider", voiceMeta.provider)
      }
    }

    const planModelCredits = planDetails?.model_credits_per_minute?.[modelSlug] || 0

    const modelMaxCredits = Math.max(
      ...(Object.values(planDetails?.model_credits_per_minute || {}).filter(Boolean) as number[])
    )

    setMaxCredits(Math.max(elevenlabsCost, gcpCost) + modelMaxCredits)

    const selectedModelAttributes = modelAttributes[modelSlug] || { speed: 300, intelligence: 70 }
    setModelCreditsPerMinute(planModelCredits)
    setModelSpeed(selectedModelAttributes.speed)
  }, [planDetails, modelSlug, voiceCloneId, voiceId])

  useEffect(() => {
    setModelCreditsWidth(`${(modelCreditsPerMinute / maxCredits) * 100}%`)
    setVoiceCreditsWidth(`${(voiceCreditsPerMinute / maxCredits) * 100}%`)
    setModelSpeedWidth(`${(100 * modelSpeed) / 800}%`)
    setVoiceSpeedWidth(`${(100 * voiceSpeed) / 800}%`)
  }, [modelCreditsPerMinute, voiceCreditsPerMinute, maxCredits, modelSpeed, voiceSpeed])

  if (!planDetails) {
    return null
  }

  return (
    <div className={`mt-4 w-full ${showCredits ? "md:grid md:grid-cols-2 md:gap-4 md:space-y-0" : ""} space-y-5`}>
      {showCredits && (
        <div className="bg-white p-4 rounded-xl shadow-sm">
          <div className="mb-2 flex items-center">
            <span className="text-base font-semibold text-gray-800">
              Credits per minute:{" "}
              <span className="text-purple-600">{modelCreditsPerMinute + voiceCreditsPerMinute}</span>
            </span>
            <Tooltip content="Credits are used to pay for AI and voice services. Higher quality services use more credits per minute of call time. Your plan includes a set number of credits each month.">
              <InfoCircle className="ml-2 text-gray-500 cursor-help" width="18" height="18" />
            </Tooltip>
          </div>
          <div className="space-y-3">
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-sm font-medium text-gray-600">AI</span>
                <span className="text-sm font-medium text-lime-600">{modelCreditsPerMinute}</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full bg-lime-500 rounded-full" style={{ width: modelCreditsWidth }} />
              </div>
            </div>
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-sm font-medium text-gray-600">Voice</span>
                <span className="text-sm font-medium text-teal-600">{voiceCreditsPerMinute}</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full bg-teal-500 rounded-full" style={{ width: voiceCreditsWidth }} />
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white p-4 rounded-xl shadow-sm">
        <div className="flex items-center mb-2">
          <h2 className="text-xl font-semibold text-gray-800">Latency</h2>
          <Tooltip content="Latency is the response time in conversations. Lower latency means more natural-feeling calls with less waiting between responses.">
            <InfoCircle className="ml-2 text-gray-500 cursor-help" width="18" height="18" />
          </Tooltip>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          This is the response time of your AI. A lower latency means a more natural, human-like conversation. Total
          latency is the sum of the AI and Voice latency.
        </p>
        <div className="mb-2 flex items-center">
          <span className="text-base font-semibold text-gray-800">
            Total Latency: <span className="text-purple-600">{modelSpeed + voiceSpeed}ms</span>
            <span className="text-sm font-normal text-gray-500 ml-1">(lower is better)</span>
          </span>
        </div>
        <div className="space-y-3">
          <div>
            <div className="flex justify-between mb-1">
              <span className="text-sm font-medium text-gray-600">AI</span>
              <span className="text-sm font-medium text-green-600">{modelSpeed}ms</span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-green-600 rounded-full" style={{ width: modelSpeedWidth }} />
            </div>
          </div>
          <div>
            <div className="flex justify-between mb-1">
              <span className="text-sm font-medium text-gray-600">Voice</span>
              <span className="text-sm font-medium text-red-600">{voiceSpeed}ms</span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-red-500 rounded-full" style={{ width: voiceSpeedWidth }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// New component for AI intelligence visualization to be used in AI Settings card
function AIIntelligenceVisualization({ modelSlug }: { modelSlug: string }) {
  const [modelIntelligence, setModelIntelligence] = useState(50)
  const [modelIntelligenceWidth, setModelIntelligenceWidth] = useState("")
  const [modelSpeed, setModelSpeed] = useState(50)
  const [modelSpeedWidth, setModelSpeedWidth] = useState("")

  useEffect(() => {
    const selectedModelAttributes = modelAttributes[modelSlug] || { speed: 300, intelligence: 70 }
    setModelIntelligence(selectedModelAttributes.intelligence)
    setModelSpeed(selectedModelAttributes.speed)

    setModelIntelligenceWidth(`${selectedModelAttributes.intelligence}%`)
    setModelSpeedWidth(`${(100 * selectedModelAttributes.speed) / 800}%`)
  }, [modelSlug])

  return (
    <div className="space-y-4">
      <div className="bg-white p-4 flex flex-col gap-2">
        <div className="flex flex-col">
          <div className="flex justify-between mb-1">
            <span className="text-sm font-medium text-gray-600">Intelligence</span>
            <span className="text-sm font-medium text-purple-600">{modelIntelligence}%</span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div className="h-full bg-purple-500 rounded-full" style={{ width: modelIntelligenceWidth }} />
          </div>
        </div>

        <div className="flex flex-col">
          <div className="flex justify-between mb-1">
            <span className="text-sm font-medium text-gray-600">Speed</span>
            <span className="text-sm font-medium text-green-600">{modelSpeed}ms</span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div className="h-full bg-green-600 rounded-full" style={{ width: modelSpeedWidth }} />
          </div>
        </div>
      </div>
    </div>
  )
}

// New component for Voice realism visualization to be used in Voice Settings card
function VoiceRealismVisualization({ voiceCloneId, voiceId }: { voiceCloneId: string | null; voiceId: string | null }) {
  const [voiceRealism, setVoiceRealism] = useState(50)
  const [voiceRealismWidth, setVoiceRealismWidth] = useState("")
  const [voiceSpeed, setVoiceSpeed] = useState(0)
  const [voiceSpeedWidth, setVoiceSpeedWidth] = useState("")

  useEffect(() => {
    if (voiceCloneId) {
      setVoiceRealism(voiceAttributes["elevenlabs"].realism)
      setVoiceSpeed(voiceAttributes["elevenlabs"].speed)
    } else if (voiceId) {
      const voiceMeta = findVoiceById(voiceId)
      if (!voiceMeta) {
        console.error("Voice not found")
        return
      }

      if (voiceMeta.provider === "gcp") {
        setVoiceRealism(voiceAttributes["gcp"].realism)
        setVoiceSpeed(voiceAttributes["gcp"].speed)
      } else if (voiceMeta.provider === "elevenlabs") {
        setVoiceRealism(voiceAttributes["elevenlabs"].realism)
        setVoiceSpeed(voiceAttributes["elevenlabs"].speed)
      } else {
        console.error("Unknown voice provider", voiceMeta.provider)
      }
    }

    setVoiceRealismWidth(`${voiceRealism}%`)
    setVoiceSpeedWidth(`${(100 * voiceSpeed) / 800}%`)
  }, [voiceCloneId, voiceId, voiceRealism])

  return (
    <div className="bg-white p-4 rounded-lg">
      <div className="space-y-4">
        <div>
          <div className="flex justify-between mb-1">
            <span className="text-sm font-medium text-gray-600">Realism</span>
            <span className="text-sm font-medium text-blue-600">{voiceRealism}%</span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div className="h-full bg-blue-600 rounded-full" style={{ width: voiceRealismWidth }} />
          </div>
        </div>

        <div>
          <div className="flex justify-between mb-1">
            <span className="text-sm font-medium text-gray-600">Latency</span>
            <span className="text-sm font-medium text-green-600">{voiceSpeed}ms</span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div className="h-full bg-green-600 rounded-full" style={{ width: voiceSpeedWidth }} />
          </div>
        </div>
      </div>
    </div>
  )
}

export function CurrentModel({ reload }: { reload: number }) {
  const vibratoClient: VibratoClient = useContext(VibratoClientContext)
  const [, setModelSlug] = useState<string>("")
  const [displayName, setDisplayName] = useState<string>("")

  useEffect(() => {
    vibratoClient
      .getCallerSettings()
      .then((data) => {
        if (data.ai_model_slug) {
          setModelSlug(data.ai_model_slug)
          const model = aiModels.find((m) => m.value === data.ai_model_slug)
          if (model) {
            setDisplayName(model.displayName)
          }
        }
      })
      .catch((error) => {
        console.error("Error fetching caller settings:", error)
      })
  }, [vibratoClient, reload])

  return <span>{displayName}</span>
}

export function CurrentCreditsPerMinute({ reload }: { reload: number }) {
  const vibratoClient: VibratoClient = useContext(VibratoClientContext)
  const [creditsPerMinute, setCreditsPerMinute] = useState<number>(0)
  const [modelSlug, setModelSlug] = useState<string | null>(null)
  const [voiceCloneId, setVoiceCloneId] = useState<number | null>(null)
  const [voiceId, setVoiceId] = useState<string | null>(null)

  useEffect(() => {
    vibratoClient
      .getCallerSettings()
      .then((data) => {
        setModelSlug(data.ai_model_slug || null)
        if (data.voice_id) {
          setVoiceId(data.voice_id)
        } else if (data.voice_clone) {
          setVoiceCloneId(data.voice_clone)
        }
      })
      .catch((error) => {
        console.error("Error fetching caller settings:", error)
      })
  }, [vibratoClient, reload])

  useEffect(() => {
    if (!modelSlug) {
      return
    }

    vibratoClient
      .getPlan()
      .then((data) => {
        if (!data.tts_credits_per_minute || !data.model_credits_per_minute) {
          console.error("Credits information is missing in the plan data")
          return
        }

        const elevenlabsCost = data.tts_credits_per_minute.elevenlabs
        const gcpCost = data.tts_credits_per_minute.gcp
        let voiceCreditsPerMinute = 0

        if (voiceCloneId) {
          voiceCreditsPerMinute = elevenlabsCost
        } else if (voiceId) {
          const voiceMeta = findVoiceById(voiceId)
          if (!voiceMeta) {
            console.error("Voice not found")
            return
          }

          if (voiceMeta.provider === "gcp") {
            voiceCreditsPerMinute = gcpCost
          } else if (voiceMeta.provider === "elevenlabs") {
            voiceCreditsPerMinute = elevenlabsCost
          } else {
            console.error("Unknown voice provider", voiceMeta.provider)
            return
          }
        }

        const modelCreditsPerMinute = data.model_credits_per_minute[modelSlug]
        if (!modelCreditsPerMinute) {
          console.error("Unknown model slug", modelSlug)
          return
        }

        setCreditsPerMinute(modelCreditsPerMinute + voiceCreditsPerMinute)
      })
      .catch((error) => {
        console.error("Error fetching plan:", error)
      })
  }, [modelSlug, voiceCloneId, voiceId, vibratoClient])

  return <span>{creditsPerMinute}</span>
}
